import { z } from 'zod';

export const accountIdSchema = z.union([z.string(), z.number()]);

export const accountSchema = z
    .object({
        id: accountIdSchema.nullish(),
        username: z.string().nullish(),
        account: z.string().nullish(),
        hadAccount: z.boolean().nullish(),
        partnerRedirectUrl: z.string().nullish(),
        partnerInfo: z.record(z.string(), z.unknown()).nullish(),
    })
    .passthrough();

/** PUT/PATCH success bodies may be empty; coerce before account field checks. */
function normalizeMutationResponse(data: unknown): unknown {
    if (data === null || data === undefined || data === '') {
        return {};
    }
    if (typeof data !== 'object' || Array.isArray(data)) {
        return {};
    }
    return data;
}

const mutationAccountResponseSchema = z.preprocess(normalizeMutationResponse, accountSchema);

function withCustomerType(data: unknown): unknown {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return data;
    }
    const body = data as Record<string, unknown>;
    const customerType =
        body.customerType ?? body.CustomerType ?? body.customer_type;
    if (customerType === undefined) {
        return data;
    }
    return { ...body, customerType };
}

const customerTypeSchema = z.preprocess(
    (value) => (value == null || value === '' ? null : String(value)),
    z.string().nullable()
);

/** POST /account/term */
export const acceptTermsResponseSchema = z.preprocess(
    withCustomerType,
    z
        .object({
            accountId: accountIdSchema,
            customerType: customerTypeSchema,
        })
        .passthrough()
);

/** PUT /account/:id */
export const updateAccountResponseSchema = mutationAccountResponseSchema;

export const verifyOtpPayloadSchema = z.object({
    code: z.union([z.string(), z.number()]),
    type: z.number().optional(),
});

export const resendOtpPayloadSchema = z.object({
    type: z.number(),
});

export const otpCodeSchema = z.union([z.string(), z.number()]);

/** OTP verify / resend / email-verify / phone-verify — empty or opaque success bodies */
export const otpStepResponseSchema = z.object({}).passthrough();

export const verifyOtpResponseSchema = otpStepResponseSchema;
export const resendOtpResponseSchema = otpStepResponseSchema;
export const verifyEmailResponseSchema = otpStepResponseSchema;
export const verifyPhoneResponseSchema = otpStepResponseSchema;

/** POST /account/read-terms/:id */
export const readTermsResponseSchema = z.object({}).passthrough();

/** POST /account/verify-read-terms/:id */
export const verifyReadTermsResponseSchema = z.preprocess(
    (data) => withCustomerType(normalizeMutationResponse(data)),
    z
        .object({
            customerType: customerTypeSchema,
        })
        .passthrough()
);

/** POST /account/complete/:id */
export const completeAccountResponseSchema = mutationAccountResponseSchema;

/** POST /account/image-verify/:id */
export const imageVerifyResponseSchema = z
    .object({
        token: z.string(),
    })
    .passthrough();

/** POST /account/check-complete/:id */
export const checkSumsubCompleteResponseSchema = z
    .object({
        success: z.boolean(),
        account: accountSchema.optional(),
    })
    .passthrough();

/** POST /account/verify/partner */
export const verifyPartnerResponseSchema = z
    .object({
        success: z.boolean().optional(),
    })
    .passthrough();

/** GET /imid/request-authaas-token */
export const imIdAuthTokenResponseSchema = z.object({}).passthrough();

export type AcceptTermsResponse = z.infer<typeof acceptTermsResponseSchema>;
export type UpdateAccountResponse = z.infer<typeof updateAccountResponseSchema>;
export type OtpStepResponse = z.infer<typeof otpStepResponseSchema>;
export type ReadTermsResponse = z.infer<typeof readTermsResponseSchema>;
export type VerifyReadTermsResponse = z.infer<typeof verifyReadTermsResponseSchema>;
export type CompleteAccountResponse = z.infer<typeof completeAccountResponseSchema>;
export type ImageVerifyResponse = z.infer<typeof imageVerifyResponseSchema>;
export type CheckSumsubCompleteResponse = z.infer<typeof checkSumsubCompleteResponseSchema>;
export type VerifyPartnerResponse = z.infer<typeof verifyPartnerResponseSchema>;
export type ImIdAuthTokenResponse = z.infer<typeof imIdAuthTokenResponseSchema>;

export function parseRegistrationResponse<T extends z.ZodTypeAny>(
    schema: T,
    data: unknown,
    context?: string
): z.infer<T> {
    const result = schema.safeParse(data);
    if (!result.success) {
        const message = result.error.issues
            .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
            .join('; ');
        throw new Error(
            context ? `Invalid ${context} response: ${message}` : `Invalid API response: ${message}`
        );
    }
    return result.data;
}
